/**
 * "Verificação de identidade" on the rider record (D726, doc 29 §6): the badge the rider holds, every attempt (level, verdict and reason from the central
 * vocabulary, the provider with its version, the national id masked, the document type, when, the expiry), how many OTHER accounts verified the same national
 * id (a fraud signal - the digest itself never leaves the server), and the level-2 images: held until the governed retention deletes them, and opened only by
 * an operator with the privacy permission (`identity_verification:view_media`), each opening logged in the account history before the image leaves.
 */
import { type ReactElement, useState } from 'react';
import { type AdminVerificationAttemptView, type DirectoryUser } from '@vello/api-client';
import { Badge, Button, Ink, RecordRow, RecordSection, Stack, Text } from '@vello/web-theme';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { PersonCard, useCardRead } from './person-card';
import { usePersonFormat } from './person-format';

const statusTone = (status: string): 'success' | 'danger' | 'warning' | 'neutral' => (status === 'VERIFIED' ? 'success' : status === 'REFUSED' ? 'danger' : status === 'UNAVAILABLE' ? 'warning' : 'neutral');

export function PersonVerificationCard({ user }: { user: DirectoryUser }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();
  const { state, reload } = useCardRead(() => runtime.identityVerification.get(user.id), [runtime, user.id]);

  return (
    <PersonCard title={t('prec.idv.title')} what={t('prec.what.idv')} state={state} reload={reload} isEmpty={(r) => r.attempts.length === 0} empty={t('prec.idv.empty')}>
      {(r) => (
        <>
          <RecordSection>
            <RecordRow dense>
              {r.badge.level > 0 ? <Badge tone="success">{t(`prec.idv.level.${r.badge.level}`)}</Badge> : <Ink strong>{v('identity.verification_badge', r.badge.state)}</Ink>}
              <span className="vello-grow" />
              {r.badge.expiresAt !== undefined && <Ink mono>{t('prec.idv.validUntil', { date: fmt.day(r.badge.expiresAt) })}</Ink>}
            </RecordRow>
            {r.sameNationalIdOtherAccounts > 0 && (
              <Text as="p" role="caption" tone="warning" data-testid="prec-idv-same-id">
                {t('prec.idv.sameNationalId', { count: fmt.count(r.sameNationalIdOtherAccounts) })}
              </Text>
            )}
          </RecordSection>
          <RecordSection>
            <Text as="h3" role="label">
              {t('prec.idv.attempts')}
            </Text>
            {r.attempts.map((a) => (
              <Attempt key={a.id} userId={user.id} attempt={a} mediaViewable={r.mediaViewable} />
            ))}
          </RecordSection>
        </>
      )}
    </PersonCard>
  );
}

function Attempt({ userId, attempt: a, mediaViewable }: { userId: string; attempt: AdminVerificationAttemptView; mediaViewable: boolean }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v, fail } = useVocab();
  const fmt = usePersonFormat();
  const [image, setImage] = useState<{ kind: 'document' | 'selfie'; src: string } | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const open = async (kind: 'document' | 'selfie'): Promise<void> => {
    setError(undefined);
    const res = await runtime.identityVerification.media(userId, a.id, kind);
    if (res.ok) setImage({ kind, src: `data:${res.value.contentType};base64,${res.value.bytesBase64}` });
    else setError(fail(res.error));
  };
  return (
    <Stack gap={2} data-testid={`prec-idv-attempt-${a.id}`}>
      <RecordRow dense>
        <Ink strong>{t('prec.idv.level', { level: String(a.level) })}</Ink>
        <Badge tone={statusTone(a.status)}>{v('identity.verification_status', a.status)}</Badge>
        <span className="vello-grow" />
        <Ink mono>{fmt.when(a.decidedAt)}</Ink>
      </RecordRow>
      <Text as="p" role="caption" tone="secondary">
        {v('identity.verification_reason', a.reasonCode)}
        {a.nationalIdMasked !== undefined ? ` · ${a.nationalIdMasked}` : ''}
        {a.expiresAt !== undefined ? ` · ${t('prec.idv.validUntil', { date: fmt.day(a.expiresAt) })}` : ''}
      </Text>
      <Text as="p" role="caption" tone="secondary" mono>
        {t('prec.idv.provider', { provider: a.providerId, version: a.providerVersion })}
      </Text>
      {a.media !== null && (
        <>
          <Text as="p" role="caption" tone="secondary">
            {a.media.held ? t('prec.idv.images.held', { date: fmt.when(a.media.purgeAfter) }) : a.media.purgedAt !== undefined ? t('prec.idv.images.purged', { date: fmt.when(a.media.purgedAt) }) : t('prec.idv.images.notKept')}
          </Text>
          {a.media.held &&
            (mediaViewable ? (
              <>
                <RecordRow dense>
                  <Button variant="secondary" size="sm" onClick={() => void open('document')} data-testid={`prec-idv-open-document-${a.id}`}>
                    {t('prec.idv.open.document')}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => void open('selfie')} data-testid={`prec-idv-open-selfie-${a.id}`}>
                    {t('prec.idv.open.selfie')}
                  </Button>
                </RecordRow>
                <Text as="p" role="caption" tone="secondary">
                  {t('prec.idv.open.logged')}
                </Text>
              </>
            ) : (
              <Text as="p" role="caption" tone="secondary" data-testid={`prec-idv-locked-${a.id}`}>
                {t('prec.idv.images.locked')}
              </Text>
            ))}
          {error !== undefined && (
            <Text as="p" role="caption" tone="danger">
              {error}
            </Text>
          )}
          {image !== undefined && <img className="vello-media-preview" src={image.src} alt={t(`prec.idv.image.alt.${image.kind}`)} data-testid={`prec-idv-image-${a.id}`} />}
        </>
      )}
    </Stack>
  );
}
